/**
 * 화면·데칼용 절차 CanvasTexture 유틸 (외부 에셋 금지 원칙).
 * 모니터(VS Code 다크 / 밝은 웹앱), 스마트폰 잠금화면, 맥북 리드 스티커 시트,
 * 머그 "WORK. STRESS. SMASH." 랩 텍스처를 캔버스로 그린다.
 * 전부 ≤1024px, SRGBColorSpace. 모듈 로드 시 DOM을 만지지 않도록 전 함수 lazy + 캐시.
 */
import * as THREE from 'three';
import { lcg } from './rng';
import { makeCanvas, toTexture } from './canvas2d';

/** 라운드 사각형 패스(구형 TS lib의 roundRect 부재 대비 자체 구현). */
function rrect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// ---- 캐시 (재빌드/리셋 시 텍스처 재생성 방지) ----
const cache = new Map<string, THREE.CanvasTexture>();
function cached(key: string, make: () => THREE.CanvasTexture): THREE.CanvasTexture {
  let t = cache.get(key);
  if (!t) {
    t = make();
    cache.set(key, t);
  }
  return t;
}

/**
 * VS Code 다크: 액티비티바 + 탐색기 + 탭바 + 줄번호 + 구조화된 구문색 토큰
 * (키워드 보라/파랑 → 식별자 청록 → 함수 노랑 → 문자열 주황) + 미니맵 + 하단 터미널 +
 * 블루 스테이터스바. 무작위 색 나열이 아니라 "코드 문장" 패턴으로 그린다.
 */
export function makeCodeScreenTexture(): THREE.CanvasTexture {
  return cached('code', () => {
    const { canvas, ctx } = makeCanvas(1024, 576);
    const rnd = lcg(0xc0de);

    // 에디터 배경
    ctx.fillStyle = '#1e1e1e';
    ctx.fillRect(0, 0, 1024, 576);

    // 액티비티 바(좌측 아이콘 열 — 활성 아이콘만 화이트)
    ctx.fillStyle = '#333338';
    ctx.fillRect(0, 0, 40, 576);
    for (let i = 0; i < 6; i++) {
      ctx.fillStyle = i === 0 ? '#ffffff' : '#7a7a85';
      ctx.fillRect(11, 18 + i * 48, 18, 18);
    }

    // 사이드바(탐색기 파일 트리)
    ctx.fillStyle = '#252528';
    ctx.fillRect(40, 0, 170, 576);
    ctx.fillStyle = '#8a8f98';
    ctx.fillRect(54, 16, 92, 10);
    for (let i = 0; i < 17; i++) {
      const indent = i % 5 === 1 || i % 5 === 2 ? 16 : 0;
      // 파일 아이콘 점 + 파일명 바.
      ctx.fillStyle = ['#e8590c', '#3b82f6', '#4ec9b0', '#f5c531'][i % 4];
      ctx.fillRect(54 + indent, 45 + i * 26, 7, 7);
      ctx.fillStyle = i % 4 === 0 ? '#a8aeb8' : '#6f7580';
      ctx.fillRect(66 + indent, 44 + i * 26, 36 + rnd() * 62, 9);
    }

    // 탭바(활성 탭 + 비활성 탭)
    ctx.fillStyle = '#2b2b30';
    ctx.fillRect(210, 0, 814, 38);
    ctx.fillStyle = '#1e1e1e';
    ctx.fillRect(210, 0, 150, 38);
    ctx.fillStyle = '#3b82f6';
    ctx.fillRect(210, 0, 150, 3);
    ctx.fillStyle = '#d0d4da';
    ctx.fillRect(226, 15, 82, 9);
    ctx.fillStyle = '#26262b';
    ctx.fillRect(362, 0, 140, 38);
    ctx.fillStyle = '#8a8f98';
    ctx.fillRect(378, 15, 70, 9);

    // 코드 본문: "키워드 → 식별자 → 연산자 → 값" 문장 구조로 토큰 배열.
    const KEYWORD = ['#c586c0', '#569cd6']; // 보라(제어) / 파랑(선언)
    const IDENT = '#9cdcfe'; // 청록 식별자
    const FUNC = '#dcdcaa'; // 노랑 함수명
    const STRING = '#ce9178'; // 주황 문자열
    const TYPE = '#4ec9b0'; // 틸 타입
    const PUNCT = '#d4d4d4'; // 회백 연산자·괄호
    let indent = 0;
    for (let l = 0; l < 19; l++) {
      const y = 58 + l * 19;
      // 줄번호 컬럼.
      ctx.fillStyle = '#5a5f68';
      ctx.fillRect(218, y, 14, 8);
      let x = 246 + indent * 26;
      if (rnd() < 0.15) {
        // 주석 라인(그린 단일 바).
        ctx.fillStyle = '#6a9955';
        ctx.fillRect(x, y, 160 + rnd() * 140, 8);
      } else {
        // 키워드(짧은 보라/파랑).
        ctx.fillStyle = KEYWORD[Math.floor(rnd() * 2)];
        ctx.fillRect(x, y, 34 + rnd() * 16, 8);
        x += 58;
        // 식별자/함수명.
        ctx.fillStyle = rnd() < 0.4 ? FUNC : IDENT;
        ctx.fillRect(x, y, 52 + rnd() * 40, 8);
        x += 104;
        // 연산자·괄호(짧은 회백).
        ctx.fillStyle = PUNCT;
        ctx.fillRect(x, y, 12, 8);
        x += 24;
        // 값: 문자열(주황) / 타입(틸) / 식별자.
        const v = rnd();
        ctx.fillStyle = v < 0.4 ? STRING : v < 0.65 ? TYPE : IDENT;
        ctx.fillRect(x, y, 60 + rnd() * 90, 8);
        if (rnd() < 0.4) {
          x += 170;
          ctx.fillStyle = PUNCT;
          ctx.fillRect(x, y, 8 + rnd() * 20, 8);
        }
      }
      if (rnd() < 0.4) indent = Math.max(0, Math.min(3, indent + (rnd() < 0.5 ? 1 : -1)));
    }

    // 미니맵(에디터 영역만).
    ctx.fillStyle = '#232327';
    ctx.fillRect(950, 38, 74, 380);
    for (let i = 0; i < 42; i++) {
      ctx.fillStyle = '#4a4e57';
      ctx.fillRect(958, 48 + i * 8.6, 8 + rnd() * 44, 3);
    }

    // 하단 통합 터미널(다크 패널 + 프롬프트 라인).
    ctx.fillStyle = '#161618';
    ctx.fillRect(210, 418, 814, 134);
    ctx.fillStyle = '#333338';
    ctx.fillRect(210, 418, 814, 2);
    ctx.fillStyle = '#a8aeb8';
    ctx.fillRect(226, 428, 62, 8); // TERMINAL 탭 라벨
    ctx.fillStyle = '#3b82f6';
    ctx.fillRect(226, 440, 62, 2);
    for (let i = 0; i < 5; i++) {
      const y = 452 + i * 19;
      ctx.fillStyle = '#4ec9b0'; // 프롬프트($) 그린
      ctx.fillRect(226, y, 12, 8);
      ctx.fillStyle = i === 3 ? '#e8590c' : '#9aa0a8'; // 한 줄은 경고 오렌지
      ctx.fillRect(246, y, 120 + rnd() * 260, 8);
    }

    // 블루 스테이터스바
    ctx.fillStyle = '#0a7acc';
    ctx.fillRect(0, 552, 1024, 24);
    ctx.fillStyle = '#e8f2fb';
    ctx.fillRect(14, 560, 58, 8);
    ctx.fillRect(90, 560, 34, 8);
    ctx.fillRect(920, 560, 88, 8);

    return toTexture(canvas);
  });
}

/** 밝은 웹앱: 브라우저 크롬 + 블루 헤더 + 리스트 행 + 우측 패널(버튼/미니 차트). */
export function makeBrowserScreenTexture(): THREE.CanvasTexture {
  return cached('browser', () => {
    const { canvas, ctx } = makeCanvas(1024, 576);
    const rnd = lcg(0xb0b5ed);

    // 브라우저 크롬(탭 + 주소창)
    ctx.fillStyle = '#dfe1e5';
    ctx.fillRect(0, 0, 1024, 64);
    ctx.fillStyle = '#f8f9fa';
    rrect(ctx, 12, 6, 190, 24, 8);
    ctx.fill();
    ctx.fillStyle = '#5f6368';
    ctx.fillRect(30, 15, 96, 7);
    ctx.fillStyle = '#ffffff';
    rrect(ctx, 96, 36, 720, 22, 11);
    ctx.fill();
    ctx.fillStyle = '#9aa0a6';
    ctx.fillRect(112, 44, 190, 7);
    for (let i = 0; i < 3; i++) {
      ctx.fillStyle = '#8b9096';
      ctx.beginPath();
      ctx.arc(24 + i * 26, 47, 7, 0, Math.PI * 2);
      ctx.fill();
    }

    // 블루 앱 헤더
    ctx.fillStyle = '#2f6fed';
    ctx.fillRect(0, 64, 1024, 84);
    ctx.fillStyle = '#ffffff';
    rrect(ctx, 28, 88, 34, 34, 8);
    ctx.fill();
    ctx.fillRect(78, 98, 150, 13);
    ctx.fillStyle = 'rgba(255,255,255,0.65)';
    for (let i = 0; i < 4; i++) ctx.fillRect(660 + i * 84, 100, 56, 10);

    // 본문 배경
    ctx.fillStyle = '#f5f6f8';
    ctx.fillRect(0, 148, 1024, 428);

    // 좌측 사이드바 내비(화이트 컬럼 + 메뉴 항목, 활성 항목 하이라이트).
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 148, 176, 428);
    ctx.fillStyle = '#e7eaef';
    ctx.fillRect(176, 148, 1, 428);
    for (let i = 0; i < 8; i++) {
      const y = 174 + i * 40;
      if (i === 1) {
        ctx.fillStyle = '#e8effc'; // 활성 메뉴 배경
        rrect(ctx, 10, y - 8, 156, 30, 6);
        ctx.fill();
      }
      ctx.fillStyle = i === 1 ? '#2f6fed' : '#9aa0a6';
      ctx.fillRect(22, y, 12, 12);
      ctx.fillStyle = i === 1 ? '#2f6fed' : '#7a828c';
      ctx.fillRect(44, y + 2, 60 + rnd() * 46, 8);
    }

    // 중앙 행 테이블 카드(헤더 행 + 데이터 행 + 상태 칩).
    ctx.fillStyle = '#ffffff';
    rrect(ctx, 196, 168, 470, 388, 10);
    ctx.fill();
    ctx.fillStyle = '#3c4048';
    ctx.fillRect(216, 186, 130, 11);
    // 컬럼 헤더 행.
    ctx.fillStyle = '#f0f2f5';
    ctx.fillRect(216, 210, 430, 22);
    ctx.fillStyle = '#8a9099';
    ctx.fillRect(228, 217, 60, 8);
    ctx.fillRect(330, 217, 90, 8);
    ctx.fillRect(470, 217, 70, 8);
    ctx.fillRect(590, 217, 40, 8);
    const chips = ['#2f6fed', '#34a06e', '#e8590c'];
    for (let i = 0; i < 8; i++) {
      const y = 244 + i * 37;
      ctx.fillStyle = '#eef0f3';
      ctx.fillRect(216, y + 26, 430, 1);
      ctx.fillStyle = '#c9d4f4';
      ctx.beginPath();
      ctx.arc(228, y + 8, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#4a4f57';
      ctx.fillRect(248, y + 3, 90 + rnd() * 70, 9);
      ctx.fillStyle = '#aab0bc';
      ctx.fillRect(470, y + 3, 60 + rnd() * 30, 8);
      ctx.fillStyle = chips[i % 3];
      rrect(ctx, 588, y, 56, 15, 7);
      ctx.fill();
    }

    // 우측 상세 패널(제목/본문/버튼)
    ctx.fillStyle = '#ffffff';
    rrect(ctx, 686, 168, 314, 244, 10);
    ctx.fill();
    ctx.fillStyle = '#3c4048';
    ctx.fillRect(706, 190, 148, 12);
    ctx.fillStyle = '#c3c8d1';
    for (let i = 0; i < 6; i++) ctx.fillRect(706, 218 + i * 20, 170 + rnd() * 100, 8);
    ctx.fillStyle = '#2f6fed';
    rrect(ctx, 706, 362, 122, 30, 8);
    ctx.fill();

    // 우측 하단 미니 차트 카드
    ctx.fillStyle = '#ffffff';
    rrect(ctx, 686, 428, 314, 128, 10);
    ctx.fill();
    for (let i = 0; i < 9; i++) {
      const h = 16 + rnd() * 66;
      ctx.fillStyle = i % 3 === 0 ? '#2f6fed' : '#7fa3f2';
      ctx.fillRect(706 + i * 32, 540 - h, 18, h);
    }

    return toTexture(canvas);
  });
}

/** 맥북 리드 스티커 시트(투명 배경, 화이트 다이컷 테두리 데칼 7종). */
export function makeStickerSheet(): THREE.CanvasTexture {
  return cached('stickers', () => {
    const { canvas, ctx } = makeCanvas(512, 512);

    const sticker = (x: number, y: number, rot: number, draw: () => void): void => {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rot);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 10;
      draw();
      ctx.restore();
    };

    // 주황 원형(중앙 화이트 글리프)
    sticker(112, 122, 0.15, () => {
      ctx.beginPath();
      ctx.arc(0, 0, 58, 0, Math.PI * 2);
      ctx.fillStyle = '#e8590c';
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(-18, -18, 36, 36);
    });
    // 블루 라운드 사각(플레이 트라이앵글)
    sticker(312, 96, -0.25, () => {
      rrect(ctx, -46, -46, 92, 92, 18);
      ctx.fillStyle = '#2f6fed';
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(-14, -20);
      ctx.lineTo(22, 0);
      ctx.lineTo(-14, 20);
      ctx.closePath();
      ctx.fill();
    });
    // 그린 육각형(도넛 홀)
    sticker(430, 226, 0.3, () => {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        const px = Math.cos(a) * 52;
        const py = Math.sin(a) * 52;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fillStyle = '#2fa06e';
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(0, 0, 15, 0, Math.PI * 2);
      ctx.fill();
    });
    // 옐로 별
    sticker(142, 306, -0.2, () => {
      ctx.beginPath();
      for (let i = 0; i < 10; i++) {
        const r = i % 2 === 0 ? 56 : 26;
        const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
        const px = Math.cos(a) * r;
        const py = Math.sin(a) * r;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fillStyle = '#f5c531';
      ctx.fill();
      ctx.stroke();
    });
    // 다크 원형(화이트 눈 두 개 — 마스코트 느낌)
    sticker(330, 336, 0.1, () => {
      ctx.beginPath();
      ctx.arc(0, 0, 50, 0, Math.PI * 2);
      ctx.fillStyle = '#33363d';
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(-16, -6, 10, 0, Math.PI * 2);
      ctx.arc(16, -6, 10, 0, Math.PI * 2);
      ctx.fill();
    });
    // 퍼플 라운드 사각(화이트 바 글리프)
    sticker(152, 438, 0.35, () => {
      rrect(ctx, -50, -32, 100, 64, 14);
      ctx.fillStyle = '#7c5cd6';
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(-30, -7, 60, 14);
    });
    // 민트 원형 소형
    sticker(438, 420, -0.15, () => {
      ctx.beginPath();
      ctx.arc(0, 0, 36, 0, Math.PI * 2);
      ctx.fillStyle = '#3fb8af';
      ctx.fill();
      ctx.stroke();
    });

    return toTexture(canvas);
  });
}

/** 스마트폰 잠금화면(다크 그라디언트 + 시계 + 앱 아이콘 + 독). */
export function makePhoneScreenTexture(): THREE.CanvasTexture {
  return cached('phone', () => {
    const { canvas, ctx } = makeCanvas(256, 512);
    const rnd = lcg(0xf03e);

    const grad = ctx.createLinearGradient(0, 0, 0, 512);
    grad.addColorStop(0, '#1b2550');
    grad.addColorStop(1, '#3d1d5c');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 256, 512);

    // 상태바
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.fillRect(14, 12, 30, 6);
    ctx.fillRect(196, 12, 18, 6);
    rrect(ctx, 222, 11, 20, 9, 3);
    ctx.fill();

    // 시계 + 날짜
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 58px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('9:41', 128, 110);
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.fillRect(88, 128, 80, 8);

    // 앱 아이콘 그리드 3x4
    const iconColors = ['#e8590c', '#2f6fed', '#34a06e', '#f5c531', '#7c5cd6', '#3fb8af', '#d64550', '#8a8f98'];
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 4; c++) {
        ctx.fillStyle = iconColors[Math.floor(rnd() * iconColors.length)];
        rrect(ctx, 22 + c * 56, 176 + r * 66, 42, 42, 11);
        ctx.fill();
      }
    }

    // 독
    ctx.fillStyle = 'rgba(255,255,255,0.18)';
    rrect(ctx, 14, 428, 228, 62, 18);
    ctx.fill();
    for (let c = 0; c < 4; c++) {
      ctx.fillStyle = iconColors[(c * 2 + 1) % iconColors.length];
      rrect(ctx, 26 + c * 56, 438, 42, 42, 11);
      ctx.fill();
    }

    return toTexture(canvas);
  });
}

/** 머그 랩: 블랙 매트 바탕 + "WORK. STRESS. SMASH." 3단 화이트 타이포(사무직 전반). */
export function makeMugLabelTexture(): THREE.CanvasTexture {
  return cached('mug', () => {
    const { canvas, ctx } = makeCanvas(512, 256);
    ctx.fillStyle = '#141416';
    ctx.fillRect(0, 0, 512, 256);
    ctx.fillStyle = '#f2f3f5';
    ctx.textAlign = 'center';
    ctx.font = 'bold 40px Arial, sans-serif';
    ctx.fillText('WORK.', 256, 92);
    ctx.fillText('STRESS.', 256, 142);
    ctx.fillText('SMASH.', 256, 192);
    const tex = toTexture(canvas);
    // CylinderGeometry는 u=0 이음새가 +z(정면)에 오므로 캔버스 중앙(텍스트)이 정면을 보도록 반 바퀴 오프셋.
    tex.wrapS = THREE.RepeatWrapping;
    tex.offset.x = 0.5;
    return tex;
  });
}
